import { Fragment, useEffect, useState } from "react";
import experienceDetailBG from "assets/images/others/host-side-images/experienceDetailBG.png";
import { useNavigate } from "react-router-dom";
import Breadcrumb, { BreadcrumbLink } from "components/breadcrumbs";
import { siteRoutes } from "utils/helpers/enums/routes";
import useHost from "../useHooks";
import { EventDTO } from "utils/helpers/models/shared/event.dto";
import useUtils from "hooks/useUtils";
import { getFilePathWithBackendUrl } from "utils/helpers/common/http-methods";
import { EVENT_STATUS } from "utils/helpers/enums/shared.enum";
import { confirmationPopup } from "utils/helpers/common/alert-service";
import { ReactComponent as ScanIcon } from "assets/images/svgs/scan.svg";
import ScannerCameraPopup from "components/popups/scanner";
import QrScanner from "components/qrcode-scanner";
import { AiOutlineClose } from "react-icons/ai";
import WorkshopSessionPopup from "components/popups/workshop-session";
import CancelEventModal from "./component/CancelEventModal";
import useStore from "hooks/useStore";
import ImageCarousel from "components/image-carousel";
import ExperienceDetailHeader from "components/experience-detail-header";

const ExperienceDetail = () => {
  const breadcrumbLinks: BreadcrumbLink[] = [
    { title: "Home ", path: siteRoutes.home },
    { title: "Experiences Detail", path: siteRoutes.experienceDetail },
  ];
  const { isProfileCompleted } = useStore();
  const [isScannerModalOpen, setIsScannerModalOpen] = useState(false);
  const [isQrScannerOpen, setIsQrScannerOpen] = useState(false);
  const [scannedData, setScannedData] = useState(null);
  const [workshopSessionModal, setWorkshopSessionModal] = useState(false);
  const [cancelEventModalOpen, setCancelEventModalOpen] = useState(false);

  const closeModals = () => {
    setIsScannerModalOpen(false);
    setIsQrScannerOpen(false);
  };
  const navigate = useNavigate();
  const { getEventById, duplicateEvent, cancelEvent } = useHost();
  const { getQueryParams } = useUtils();
  const params = getQueryParams();
  const [event, setEvent] = useState<EventDTO>(new EventDTO());
  let hostEventData: any;

  // Check profile completion on mount - redirect to home if not completed
  useEffect(() => {
    const profileCompleted = isProfileCompleted();
    if (!profileCompleted) {
      navigate(siteRoutes.home, { replace: true });
    }
  }, [isProfileCompleted, navigate]);

  useEffect(() => {
    getEventById(params?.id, setEvent);
  }, []);

  const handleDuplicateEvent = async () => {
    const confirmation = await confirmationPopup(
      "Are you sure you want to DUPLICATE this event?"
    );
    if (confirmation.isConfirmed) {
      duplicateEvent(event.id);
    }
  };

  const goToEdit = () => {
    navigate(`${siteRoutes.createExperience}?id=${event.id}`);
  };

  const handleEventInProcess = (): boolean => {
    //if today's current time is between start and end time of the workshop session then return true
    return event.workshopSessions.some((session) => {
      const startTime = new Date(session.startDateTime);
      const endTime = new Date(session.endDateTime);
      const currentTime = new Date();
      return currentTime >= startTime && currentTime <= endTime;
    });
  };

  const handleScanAgain = () => {
    setScannedData(null);
    setTimeout(() => {
      setIsQrScannerOpen(true);
    }, 100);
  };

  const handleModalClose = () => {
    setScannedData(null);
    setIsScannerModalOpen(true);
  };

  const now = new Date();
  const sessions = event.workshopSessions || [];

  // Sort sessions by startDateTime to get the actual first session
  const sortedSessions = [...sessions].sort((a, b) => 
    new Date(a.startDateTime).getTime() - new Date(b.startDateTime).getTime()
  );

  // Start check - using first session (earliest) start date and time
  const firstSession = sortedSessions.length > 0 ? sortedSessions[0] : null;
  const firstStart = firstSession ? new Date(firstSession.startDateTime) : null;
  const hasEventStarted =
    firstStart && !isNaN(firstStart.getTime()) && now >= firstStart;

  // Sort sessions by endDateTime to get the actual last session
  const sortedByEnd = [...sessions].sort((a, b) => 
    new Date(b.endDateTime).getTime() - new Date(a.endDateTime).getTime()
  );

  // End check - using last session (latest) end date and time
  const lastSession = sortedByEnd.length > 0 ? sortedByEnd[0] : null;
  const lastEnd = lastSession ? new Date(lastSession.endDateTime) : null;
  const hasEventEnded = lastEnd && !isNaN(lastEnd.getTime()) && now >= lastEnd;

  const visibleSessions = event.workshopSessions.slice(0, 3);
  const hasMoreSessions = event.workshopSessions.length > 3;

  const goToAttendess = () => {
    navigate(siteRoutes.attendess, {
      state: { eventData: event },
    });
  };

  return (
    <div>
      {event ? (
        <Fragment>
          <ExperienceDetailHeader
            event={event}
            breadcrumb={<Breadcrumb links={breadcrumbLinks} />}
            // onHostClick={goToHostDetail}
            onSeeMoreSessions={() => setWorkshopSessionModal(true)}
            visibleSessions={visibleSessions}
            hasMoreSessions={hasMoreSessions}
            onAttendeesClick={goToAttendess}
            gradientClass="bg-gradient"
          />

          <div className="container my-5">
            <div className="flex flex-wrap sm:flex-nowrap justify-between items-center gap-6 ">
              <div className="text-blackie text-3xl">Overview</div>
              <div className="flex gap-4">
                {event.eventStatus === EVENT_STATUS.POSTED &&
                  handleEventInProcess() && (
                    <button
                      onClick={() => setIsScannerModalOpen(true)}
                      className="px-5 py-3 flex items-center gap-2 rounded-full text-white bg-rsvpgray cursor-pointer"
                    >
                      <ScanIcon />
                      Scan
                    </button>
                  )}

                {hasEventEnded == true &&
                  event.eventStatus == EVENT_STATUS.COMPLETED && (
                    <button
                      className="px-5 py-3 flex gap-2 rounded-full text-white bg-primaryBlueBg cursor-pointer"
                      onClick={() => {
                        navigate(siteRoutes.reviewsCups, {
                          state: { eventData: event },
                        });
                      }}
                    >
                      View Rating
                    </button>
                  )}

                {event.eventStatus == EVENT_STATUS.POSTED &&
                  !hasEventStarted &&
                  !hasEventEnded && (
                    <button
                      className="px-5 py-3 flex items-center justify-center gap-2 rounded-full text-white bg-primaryBlueBg cursor-pointer"
                      onClick={() => {
                        setCancelEventModalOpen(true);
                      }}
                    >
                      Cancel Event
                    </button>
                  )}

                <button
                  className="px-5 py-3 flex gap-2 rounded-full text-white bg-primaryBlueBg cursor-pointer"
                  onClick={handleDuplicateEvent}
                >
                  Duplicate Event
                </button>
              </div>
            </div>
            <div className="w-full flex flex-wrap md:flex-nowrap my-5 md:flex-row flex-col-reverse md:gap-6">
              <div className="w-full md:w-[60%] mt-5 md:mt-0">
                <div>
                  <h3 className="text-blackie text-xl font-medium mb-2">
                    About Experience
                  </h3>
                  <p className="text-lightBlackText text-base">
                    {event?.description || "Description"}
                  </p>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 my-6">
                  <div>
                    <h3 className="text-blackie text-xl font-medium mb-2">
                      Age Group
                    </h3>
                    <p className="text-lightBlackText text-base capitalize">
                      {event?.ageGroup
                        ? event.ageGroup.replace(/_/g, " ")
                        : "Age Group"}
                    </p>
                  </div>
                  <div>
                    <div className="mb-4">
                      <h3 className="text-blackie text-xl font-medium mb-2">
                        Language
                      </h3>
                      <div className="flex gap-2">
                        <span className="px-3 py-1 bg-fade text-blackie rounded-lg text-base">
                          {event?.language || "Language"}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-blackie text-xl font-medium mb-2">
                      Skill Level
                    </h3>
                    <p className="text-lightBlackText text-base capitalize">
                      {event?.skillLevel.replaceAll("_", " ") || "Skill Level"}
                    </p>
                  </div>
                </div>
                <div>
                  <h2 className="text-2xl font-medium text-blackie mb-2">
                    Details
                  </h2>

                  <div>
                    <h3 className="text-blackie text-xl font-medium mb-2">
                      Learning Activity
                    </h3>
                    <p className="text-lightBlackText text-base">
                      {event?.description || ""}
                    </p>
                  </div>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 my-6">
                    <div>
                      <h3 className="text-blackie text-xl font-medium mb-2">
                        Core Concepts
                      </h3>
                      <p className="text-lightBlackText text-base uppercase">
                        {/* {event.coreConcept.join(", ")} */}
                        {event.coreConcepts
                          ?.map((item: any) => item.name)
                          .join(", ")}
                      </p>
                    </div>
                    <div>
                      <h3 className="text-blackie text-xl font-medium mb-2">
                        Parent Supervision
                      </h3>
                      <p className="text-lightBlackText text-base">
                        {event?.parentSupervision ? "Yes" : "No"}
                      </p>
                    </div>
                    {event?.specialInstructions && (
                      <div>
                        <h3 className="text-blackie text-xl font-medium mb-2">
                          Special Instructions
                        </h3>
                        <p className="text-lightBlackText text-base">
                          {event?.specialInstructions || ""}
                        </p>
                      </div>
                    )}
                    {event?.specialInstructions && (
                      <div>
                        <h3 className="text-blackie text-xl font-medium mb-2">
                          Physical Consideration
                        </h3>
                        <ul className="text-lightBlackText text-base list-disc list-inside">
                          {event?.physicalConsideration || ""}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <h2 className="text-2xl font-semibold text-blackie mb-6">
                    Mission
                  </h2>
                  <div>
                    <h3 className="text-blackie text-xl font-medium mb-2">
                      Mission
                    </h3>
                    <p className="text-lightBlackText text-base">
                      {event?.host?.mission || ""}
                    </p>
                  </div>
                  <div>
                    <h3 className="text-blackie text-xl font-medium mb-2 mt-3">
                      About
                    </h3>
                    <p className="text-lightBlackText text-base">
                      {event?.host?.about || ""}
                    </p>
                  </div>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 my-6">
                    <div className="">
                      <p className="text-blackie  font-medium text-xl">
                        Business Name
                      </p>
                      <p className="text-lightBlackText text-base">
                        {event?.host?.businessName || ""}
                      </p>
                    </div>

                    {event?.host?.businessWebsite && (
                      <div className="">
                        <p className="text-blackie text-xl font-medium ">
                          Business Website
                        </p>
                        <a
                          href={event?.host?.businessWebsite || ""}
                          className="text-blue-600 text-base"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {event?.host?.businessWebsite || ""}
                        </a>
                      </div>
                    )}
                  </div>
                  <div>
                    {event?.rsvp && (
                      <div className="flex flex-col items-center  justify-center gap-2 rounded-lg w-[193px] h-[217px] bg-white p-3 rsvp-card-shadow ">
                        <img
                          className="w-[110px] h-[110px] rounded-full object-cover"
                          src={getFilePathWithBackendUrl(
                            (event as any)?.rsvp?.profilePicUrl?.fileDownloadUri
                          )}
                          alt="RSVP"
                        />
                        <div className="text-center">
                          <p className="font-medium text-lg text-blackie">
                            RSVP
                          </p>
                          <p className=" text-blackie text-base capitalize">{`${event?.rsvp?.firstName} ${event?.rsvp?.lastName}`}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="w-full md:w-[40%] ">
                <div className="flex flex-col">
                  <h3 className="text-blackie text-xl font-medium mb-4">
                    Event Images
                  </h3>
                  <ImageCarousel
                    images={event?.eventPicsUrl || []}
                    getImagePath={getFilePathWithBackendUrl}
                    height="h-[250px] lg:h-[300px]"
                    autoPlayInterval={6000}
                    showIndicators={true}
                  />
                </div>
              </div>
            </div>
          </div>
        </Fragment>
      ) : (
        <div>
          <div
            style={{ backgroundImage: `url(${experienceDetailBG})` }}
            className="bg-cover bg-center bg-no-repeat h-full md:h-[500px] w-full"
          ></div>
          <div className="min-h-screen"></div>
        </div>
      )}

      {isScannerModalOpen && (
        <ScannerCameraPopup
          isOpen={isScannerModalOpen}
          onClose={closeModals}
          handleOpenCamera={() => setIsQrScannerOpen(true)}
        />
      )}

      {isQrScannerOpen && (
        <QrScanner
          isOpen={isQrScannerOpen}
          onClose={closeModals}
          closeMe={() => {
            setIsQrScannerOpen(false);
          }}
          setIsScannerModalOpen={setIsScannerModalOpen}
          scannedData={scannedData}
          setScannedData={setScannedData}
          handleScanAgain={handleScanAgain}
        />
      )}

      {workshopSessionModal && (
        <WorkshopSessionPopup
          isOpen={workshopSessionModal}
          onClose={() => setWorkshopSessionModal(false)}
          sessions={event.workshopSessions}
        />
      )}
      {
        /* Cancel Event Modal */
        cancelEventModalOpen && (
          <CancelEventModal
            isOpen={cancelEventModalOpen}
            eventName={event.title}
            eventId={event.id}
            onClose={() => setCancelEventModalOpen(false)}
          />
        )
      }

      {/* Modal with Scanned Data */}
      {scannedData && (
        <div className="fixed top-0 left-0 w-full h-full bg-black bg-opacity-60 flex justify-center items-center" style={{ zIndex: 'calc(var(--z-modal) + 1)' }}>
          <div className="bg-white rounded-lg p-6 shadow-lg w-[90%] max-w-md relative">
            <button
              type="button"
              className="absolute top-2 right-3 text-xl font-bold text-gray-600 hover:text-red-500"
              onClick={handleModalClose}
            >
              <AiOutlineClose size={20} />
            </button>
            <div className="text-center">
              <img
                src={`${process.env.REACT_APP_MEDIA_BASE_URL}${scannedData.userProfile}`}
                alt="Profile"
                className="w-20 h-20 mx-auto rounded-full mb-4"
              />
              <h2 className="text-lg font-semibold">{scannedData.userName}</h2>
              <p className="text-sm text-gray-600">@{scannedData.userHandle}</p>
              {/* <div className="mt-4 text-left text-sm">
                <p><strong>Event ID:</strong> {scannedData.eventId}</p>
                <p><strong>Attendee ID:</strong> {scannedData.attendeeId}</p>
                <p><strong>Child ID:</strong> {scannedData.childId}</p>
                <p><strong>Workshop ID:</strong> {scannedData.workShopId}</p>
              </div> */}
              {/* Scan Again Button */}
              <button
                type="button"
                className="mt-5 bg-orange-500 text-white px-4 py-2 rounded hover:bg-orange-600 transition-all"
                onClick={handleScanAgain}
              >
                Scan Again
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExperienceDetail;
